/**
 * 出勤(品川・タイミー)の勤怠の確認(D-56)。その日の出勤全員を一覧にして、
 * BlueBean の値(ログイン時間・休憩時間)と、0.5h単位に丸めた自動値(拘束・休憩)を並べ、違うときだけ直して保存する。
 * 保存すると「確認済み」になる(OPレポートを取り込み直すと未確認に戻る)。手修正は shinagawa_kintai に残り、自動値に戻すと消える
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';
import { round05 } from '@core/import/buildDailySummary';
import { fetchKintai, saveKintaiCheck, type KintaiEntry } from '@core/import/importRepo';
import type { Company, DailySummary, DateStr, Kintai } from '@core/lib/types';
import { fetchOperators } from '@core/masters/operatorRepo';
import { fmtDuration, type StaffMetrics } from './calc';

type RowInput = { shift: string; brk: string; note: string };

/** 確認済みの表示(9/10 17:05 テスト管理者) */
export function fmtKintaiCheck(check: DailySummary['kintai_check'] | undefined): string | null {
  if (!check) return null;
  const at = check.at as { toDate?: () => Date } | null;
  const d = at && typeof at.toDate === 'function' ? at.toDate() : null;
  const when = d ? `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '';
  return `${when}${check.by_name ? ` ${check.by_name}` : ''}`.trim() || '確認済み';
}

const num = (s: string) => (s.trim() === '' ? NaN : Number(s));
const eq = (a: number, b: number) => Math.abs(a - b) < 1e-9;

export default function KintaiModal({
  date,
  rows,
  hasOpReport,
  checked,
  companies,
  onClose,
  onSaved,
}: {
  date: DateStr;
  rows: StaffMetrics[];
  /** その日の OPレポートが取込済みか。未取込なら確認できない(案内だけ出す) */
  hasOpReport: boolean;
  checked: DailySummary['kintai_check'] | undefined;
  companies: Company[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const [current, setCurrent] = useState<Map<string, Kintai>>(new Map());
  const [inputs, setInputs] = useState<Record<string, RowInput>>(() =>
    Object.fromEntries(rows.map((r) => [r.bb_id, { shift: String(r.shift_hours ?? round05(r.login_sec / 3600)), brk: String(r.break_hours ?? round05(r.break_sec / 3600)), note: '' }])),
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    void fetchKintai(date).then((m) => {
      if (!alive) return;
      setCurrent(m);
      setInputs((prev) => {
        const next = { ...prev };
        for (const [bbId, k] of m) if (next[bbId]) next[bbId] = { ...next[bbId]!, note: k.note ?? '' };
        return next;
      });
    });
    return () => {
      alive = false;
    };
  }, [date]);

  // 休憩の自動値 = BlueBean の休憩＋離席 を0.5h丸め(D-78)
  const autoOf = (r: StaffMetrics) => ({ shift: round05(r.login_sec / 3600), brk: round05((r.break_sec + r.away_sec) / 3600) });
  const set = (bbId: string, patch: Partial<RowInput>) => setInputs((prev) => ({ ...prev, [bbId]: { ...prev[bbId]!, ...patch } }));

  /** 入力から「自動」か「手修正」かを判定(自動値と同じでメモが無ければ自動) */
  const stateOf = (r: StaffMetrics): 'auto' | 'manual' | 'invalid' => {
    const i = inputs[r.bb_id]!;
    const s = num(i.shift);
    const b = num(i.brk);
    if (!Number.isFinite(s) || !Number.isFinite(b) || s < 0 || s > 24 || b < 0 || b > s) return 'invalid';
    const a = autoOf(r);
    return eq(s, a.shift) && eq(b, a.brk) && i.note.trim() === '' ? 'auto' : 'manual';
  };

  const invalid = useMemo(() => rows.filter((r) => stateOf(r) === 'invalid').length, [rows, inputs]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save() {
    if (!user) return;
    if (invalid > 0) {
      toast('拘束は0〜24、休憩は0以上・拘束以下の数値(h)で入力してください');
      return;
    }
    setBusy(true);
    try {
      const entries: KintaiEntry[] = [];
      for (const r of rows) {
        const i = inputs[r.bb_id]!;
        const cur = current.get(r.bb_id);
        if (stateOf(r) === 'auto') {
          if (cur) entries.push({ bb_id: r.bb_id, op_id: r.op_id, value: null });
          continue;
        }
        const value = { shift_hours: Math.round(num(i.shift) * 100) / 100, break_hours: Math.round(num(i.brk) * 100) / 100, note: i.note.trim() };
        if (cur && eq(cur.shift_hours, value.shift_hours) && eq(cur.break_hours, value.break_hours) && (cur.note ?? '') === value.note) continue;
        entries.push({ bb_id: r.bb_id, op_id: r.op_id, value });
      }
      const operators = await fetchOperators();
      await saveKintaiCheck({ date, entries, operators, companies, userId: user.id, userName: user.name ?? null });
      toast(`${date.replaceAll('-', '/')} の出勤の勤怠を確認済みにしました${entries.length > 0 ? `(変更 ${entries.length}名)` : ''}`);
      onSaved();
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const inputStyle = { width: 64, padding: '4px 6px', border: '1px solid var(--line)', borderRadius: 6, fontFamily: 'inherit', textAlign: 'right' } as const;
  const checkedLabel = fmtKintaiCheck(checked);

  if (!hasOpReport) {
    return (
      <Modal onClose={onClose} width="min(520px, 94vw)">
        <h3>出勤の勤怠を確認: {date.replaceAll('-', '/')}</h3>
        <p style={{ margin: '8px 0 0' }}>この日は OPレポートが未取込のため、勤怠の確認はできません。「CSVアップロード」で OPレポートを取り込んでから確認してください。</p>
        <div className="modal-actions">
          <button className="btn-line" onClick={onClose}>
            閉じる
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} width="min(960px, 94vw)">
      <h3>出勤の勤怠を確認: {date.replaceAll('-', '/')}</h3>
      <p className="hint" style={{ margin: '4px 0 10px' }}>
        拘束 = ログイン時間を0.5h単位に丸め、休憩 = 休憩時間を0.5h単位に丸め(0:31→0.5、0:45→1.0)。違うときだけ直して保存してください。
        {checkedLabel ? ` 前回の確認: ${checkedLabel}` : ' この日はまだ確認していません。'}
      </p>
      {rows.length === 0 && <p style={{ margin: '0 0 8px' }}>この日は出勤・タイミーの稼働がありません。確認済みにだけしておけます。</p>}
      <div style={{ overflowX: 'auto' }}>
        <table className="fit-table">
          <thead>
            <tr>
              <th>アポインター</th>
              <th className="c">出勤形態</th>
              <th className="num">ログイン時間 → 拘束(自動)</th>
              <th className="num">休憩＋離席 → 休憩(自動)</th>
              <th className="num">拘束(h)</th>
              <th className="num">休憩(h)</th>
              <th className="num">実稼働(h)</th>
              <th className="c">状態</th>
              <th>メモ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const i = inputs[r.bb_id]!;
              const a = autoOf(r);
              const st = stateOf(r);
              const worked = st === 'invalid' ? null : Math.max(0, Math.round((num(i.shift) - num(i.brk)) * 100) / 100);
              return (
                <tr key={r.bb_id}>
                  <td>
                    {r.name}
                    <span className="hint" style={{ marginLeft: 4 }}>{r.bb_id}</span>
                  </td>
                  <td className="c">{r.work_style === 'timee' ? 'タイミー' : '出勤'}</td>
                  <td className="num">
                    {fmtDuration(r.login_sec)}({(r.login_sec / 3600).toFixed(2)}h) → <b>{a.shift}</b>
                  </td>
                  <td className="num">
                    {fmtDuration(r.break_sec + r.away_sec)}({((r.break_sec + r.away_sec) / 3600).toFixed(2)}h) → <b>{a.brk}</b>
                  </td>
                  <td className="num">
                    <input inputMode="decimal" value={i.shift} onChange={(e) => set(r.bb_id, { shift: e.target.value })} style={inputStyle} className={st === 'manual' ? 'edited' : ''} />
                  </td>
                  <td className="num">
                    <input inputMode="decimal" value={i.brk} onChange={(e) => set(r.bb_id, { brk: e.target.value })} style={inputStyle} className={st === 'manual' ? 'edited' : ''} />
                  </td>
                  <td className="num">
                    <b>{worked === null ? '—' : worked}</b>
                  </td>
                  <td className="c">
                    {st === 'invalid' ? (
                      <span className="badge b-ng">入力エラー</span>
                    ) : st === 'manual' ? (
                      <>
                        <span className="badge b-ng">手修正</span>
                        <button type="button" className="txn-link" onClick={() => set(r.bb_id, { shift: String(a.shift), brk: String(a.brk), note: '' })}>
                          自動に戻す
                        </button>
                      </>
                    ) : (
                      <span className="badge b-plan">自動</span>
                    )}
                  </td>
                  <td>
                    <input value={i.note} onChange={(e) => set(r.bb_id, { note: e.target.value })} placeholder="例) 早退" style={{ ...inputStyle, width: 120, textAlign: 'left' }} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="hint" style={{ margin: '8px 0 0' }}>保存すると、この日の実稼働・報酬(参考値)・PL の人件費(未入力の日)がこの値になります。OPレポートを取り込み直しても手修正は残ります(確認は再度必要)。</p>
      <div className="modal-actions">
        <button className="btn-green" disabled={busy || invalid > 0} onClick={() => void save()}>
          {busy ? '保存中…' : '保存して確認済みにする'}
        </button>
        <button className="btn-line" onClick={onClose} disabled={busy}>
          キャンセル
        </button>
      </div>
    </Modal>
  );
}
